import {Button, createStyles, Group} from '@mantine/core';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {
  HOMEPAGE_AI_CHAT_BUTTON_ANALYTICS_ACTIONS_BY_ID,
  HomepageAiChatButtonId,
} from 'constants/analytics-products/HomepageAnalyticsConstants';
import * as NavConstants from 'constants/NavConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus} from 'llm/chat-library/ChatActions';
import {getProgressiveDisclosureCategories} from 'llm/chat-library/progressive-disclosure/ProgressiveDisclosureData';
import {openDraftChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import CardSection from './CardSection';
import {DEFAULT_SECTION_BUTTON_PROPS, HOMEPAGE_SECTION_TEST_COMPONENT} from './HomepageConstants';

const useStyles = createStyles((theme) => ({
  common: {
    paddingRight: theme.spacing.xs,
  },
  'capture-data': {
    color: theme.colors.grape[6],
  },
  'analyze-data': {
    color: theme.colors.green[6],
  },
  'write-reports': {
    color: theme.colors.teal[6],
  },
}));

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.AI;

function handleChatCreation(prompt: string, id: HomepageAiChatButtonId): void {
  trackEvent(HOMEPAGE_AI_CHAT_BUTTON_ANALYTICS_ACTIONS_BY_ID[id]);

  // Open a draft chat rather than eagerly creating a chat session. Creating a session up front
  // also creates an AsyncTask that surfaces in the homepage "Running now" section, making it look
  // like the user submitted the prompt when they only opened the curated draft. Deferring session
  // creation until the user actually submits keeps "Running now" accurate.
  openDraftChat({
    status: ChatDraftStatus.DRAFT,
    message: {text: prompt, selectedFiles: []},
    settings: {agentType: AgentType.ASSISTANT, publicDataEnabled: false},
  });

  SidepanelActions.openSection(NavConstants.SECTIONS.AI);
}

function getPromptButtons(): Array<{
  prompt: string;
  label: string;
  id: string;
  categoryId: string;
  icon: BenchlingIconName;
}> {
  return getProgressiveDisclosureCategories()
    .flatMap(({icon, id: categoryId, options}) =>
      options.map(({prompt, label, id}) => ({
        prompt,
        label,
        id,
        categoryId,
        icon,
      }))
    )
    .slice(0, 5);
}

export default function AISection(): JSX.Element {
  const {classes, cx} = useStyles();
  const promptButtons = getPromptButtons();

  return (
    <CardSection
      title="Start with chat"
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
      noBorder={true}
    >
      <Group spacing="md">
        {promptButtons.map(({prompt, label, id, categoryId, icon}) => (
          <Button
            key={id}
            onClick={() => {
              handleChatCreation(prompt, id);
            }}
            {...DEFAULT_SECTION_BUTTON_PROPS}
            leftIcon={
              <BenchlingIcon
                icon={icon}
                className={cx(classes[categoryId as keyof typeof classes], classes.common)}
              />
            }
          >
            {label}
          </Button>
        ))}
        <Button
          {...DEFAULT_SECTION_BUTTON_PROPS}
          onClick={() => {
            trackEvent(HOMEPAGE_AI_CHAT_BUTTON_ANALYTICS_ACTIONS_BY_ID.something_else);
            SidepanelActions.openSection(NavConstants.SECTIONS.AI);
          }}
        >
          Something else?
        </Button>
      </Group>
    </CardSection>
  );
}
